// ---------------------------------------------------------------------------
// Data layer — เรียก Google Apps Script ตาม docs/API.md (action ชื่อเดียวกับเอกสาร)
//  - ทุก action ยิง POST พร้อม `auth` (= localStorage["scp-acc-tk"]) ผ่าน gsClient.gsPost
//  - ฟังก์ชันใน `api` คืนข้อมูล "รูปแบบของหน้าเว็บ" (UI shape) — adapter ด้านล่างแปลงจาก response จริงของ GS
//  - IS_MOCK (ไม่ตั้ง REACT_APP_GS_URL): ใช้ mock.js (localStorage + ข้อมูลตัวอย่าง) ไม่ยิงเน็ต
//    mock handler ใช้ key ภายใน (เช่น "chars.list") และคืนรูปแบบ UI เดียวกัน
// ---------------------------------------------------------------------------
import { IS_MOCK } from "./config";
import { gsPost } from "./gsClient";
import { runMock } from "./mock";

/** ชื่อ action ของ GS (ตาม docs/API.md) — แก้ที่นี่ที่เดียวถ้าเอกสารเปลี่ยน */
export const ACTIONS = {
  me: "me.get", meUpdate: "me.update", upload: "upload",
  tags: "tags.list", charList: "characters.list", charGet: "characters.get", charMine: "characters.mine", charSave: "characters.save",
  universeList: "universes.list", universeGet: "universes.get",
  profile: "profile.get", follow: "follow.toggle", like: "like.toggle",
  chatList: "chats.list", chatCreate: "chat.create", chatGet: "chat.get", chatUpdate: "chat.update", chatDelete: "chat.delete",
  messages: "messages.list", send: "chat.send", regenerate: "chat.regenerate", messageReact: "message.react",
  memoryGet: "memory.get", memorySave: "memory.save",
  personaList: "personas.list", personaSave: "personas.save", scenarioList: "scenarios.list", scenarioSave: "scenarios.save",
  boot: "bootstrap",
  packages: "packages.list", models: "models.list", topupInit: "topup.init", topupConfirm: "topup.confirm", topupsMine: "topups.mine", pricing: "pricing.get", creditInit: "creditPack.init", creditConfirm: "creditPack.confirm", creditHistory: "creditPack.history", coinHistory: "coins.history",
  vip: "vip.status", vipClaim: "vip.claimMonthly", gifts: "gifts.list", giftSend: "gift.send",
  checkinStatus: "checkin.status", checkinClaim: "checkin.claim", promo: "promo.redeem", referralInfo: "referral.info", referralApply: "referral.apply",
  notifList: "notifications.list", notifRead: "notifications.read",
  postList: "posts.list",
  adminStats: "admin.stats", adminUsers: "admin.users.list",
};

const gs = (key, params) => gsPost(ACTIONS[key], params);
/** mock -> runMock(mockKey), จริง -> real() */
const dual = (mockKey, mockParams, real) => (IS_MOCK ? runMock(mockKey, mockParams) : real());
/** mock handler คืนรูปเดียวกับ response จริงของ GS -> ใช้ adapter ตัวเดียวกันทั้งสองโหมด (mockKey = ชื่อ action ใน docs/API.md) */
const both = (key, params = {}) => (IS_MOCK ? runMock(ACTIONS[key], params) : gs(key, params));

/** อ่านซ้ำพร้อมกัน (เช่น sidebar + หน้าแชทเรียก chats.list พร้อมกัน) -> ยิงครั้งเดียว */
const inflight = new Map();
const bothRead = (key, params = {}) => {
  const id = `${key}:${JSON.stringify(params)}`;
  if (!inflight.has(id)) inflight.set(id, both(key, params).finally(() => inflight.delete(id)));
  return inflight.get(id);
};

/** bootstrap ครั้งเดียว (แพ็กเกจ/โมเดล/ของขวัญ/ระดับ VIP) แคช 10 นาที — ล้มเหลวไม่แคช */
const BOOT_TTL = 10 * 60 * 1000;
let bootCache = null;
const loadBoot = () => {
  if (bootCache && Date.now() - bootCache.t < BOOT_TTL) return bootCache.p;
  const p = both("boot", {}).catch((e) => {
    if (bootCache?.p === p) bootCache = null;
    throw e;
  });
  bootCache = { t: Date.now(), p };
  return p;
};
/** อ่านรายการคงที่จาก bootstrap ถ้าไม่มีให้ถอยไปเรียก action เดี่ยว */
const fromBoot = async (field, fallback) => {
  try {
    const b = await loadBoot();
    if (Array.isArray(b?.[field])) return b[field];
  } catch (e) {
    /* ถอยไป action เดี่ยว */
  }
  return fallback();
};

// ---- adapters (GS -> UI shape) -------------------------------------------
let tagCache = null;
const loadTags = async () => {
  if (!tagCache) tagCache = ((await gs("tags", {})) || {}).items || [];
  return tagCache;
};
const tagNames = (ids, tags) => (ids || []).map((id) => tags.find((t) => t.id === id)?.name).filter(Boolean);
const tagIdsOf = (names, tags) => (names || []).map((n) => tags.find((t) => t.name === n)?.id).filter(Boolean);

const adaptCard = (c, tags = []) => ({
  ...c,
  tags: tagNames(c.tagIds, tags),
  creator: c.createdBy ? { id: c.createdBy, username: c.creatorName || "", displayName: c.creatorName || "" } : undefined,
  creatorId: c.createdBy,
});
const adaptChat = (c) => ({
  ...c,
  characterId: c.characterId,
  name: c.name || c.character?.name || "แชท",
  image: c.chatImage || c.character?.image || undefined,
  emoji: c.character?.emoji ?? c.emoji,
  colors: c.character?.colors ?? c.colors,
  tagline: c.character?.tagline ?? c.tagline,
});
const DEFAULT_CHAT_SETTINGS = { bannedWords: [], langGuard: true, shortcuts: [] };
/** chat.get -> รูปแบบหน้าแชท: รวมข้อมูลห้อง + ตัวละคร + persona/สถานการณ์/โน้ต/สถิติ/ความสัมพันธ์ */
const adaptChatDetail = (r) => ({
  ...adaptChat({ ...r.chat, character: r.character }),
  creatorName: r.character?.creatorName || "",
  character: r.character,
  persona: r.persona || null,
  scenario: r.scenario || null,
  model: r.model || null,
  memory: r.memory || null,
  memoryLimit: Number(r.memoryLimit) || 0,
  stats: r.stats || {},
  relationship: r.relationship || {},
  settings: { ...DEFAULT_CHAT_SETTINGS, ...(r.chat?.settings || {}) },
});
const adaptGift = (g) => ({ ...g, name: g.nameTh || g.name, emoji: g.icon || g.emoji || "🎁", coinCost: Number(g.coinCost) || 0 });
const adaptPackage = (p) => ({
  ...p,
  emoji: p.icon || p.emoji,
  tokens: p.coins ?? (Number(p.baseCoins) || 0) + (Number(p.bonusCoins) || 0),
  clubPoint: p.vipPoints ?? p.clubPoint ?? 0,
});
/** checkin.status -> รูปแบบหน้าเว็บ (todayReward = {base, bonus}) */
const adaptCheckin = (r) => ({
  ...r,
  claimedToday: !r.canClaim,
  dailyCoins: Number(r.todayReward?.base) || 0,
  bonusToday: Number(r.todayReward?.bonus) || 0,
  checkInDates: r.history || [],
});
const adaptVip = (v) => {
  const tiers = v.tiers || [];
  const tierId = v.tier?.id;
  const idx = Number.isInteger(v.tierIndex) ? v.tierIndex : Math.max(0, tiers.findIndex((t) => t.id === tierId));
  return {
    tier: v.tier || tiers[idx] || {},
    tierIndex: idx,
    tiers,
    nextTier: v.nextTier || null,
    currentMonthPoints: Number(v.currentMonthPoints) || 0,
    previousMonthPoints: Number(v.previousMonthPoints) || 0,
    effectivePoints: Number(v.effectivePoints) || 0,
    canClaimMonthly: !!v.canClaimMonthly,
    monthlyCoins: Number(v.monthlyCoins) || 0,
  };
};

const adaptUserMe = (r) => {
  const u = r?.user || {};
  const prefs = u.preferences || {};
  return {
    ...u,
    displayName: u.displayName,
    username: u.username,
    description: u.bio,
    tokensLeft: Number(u.coins) || 0,
    tier: r?.vip?.tier || u.tier || "",
    user_type: u.userType,
    // Firebase เขียน terms.accept ไว้ที่ users/<uid>/termsAcceptedAt ระดับบน ; GS เดิมเก็บใน preferences — อ่านได้ทั้งสองที่
    termsAcceptedAt: (u && u.termsAcceptedAt) || prefs.termsAcceptedAt || null,
    termsVersion: prefs.termsVersion || "",
  };
};

const mergePreferences = async (patch) => {
  const cur = (await gs("me", {}))?.user?.preferences || {};
  return gs("meUpdate", { preferences: { ...cur, ...patch } });
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = () => reject(new Error("อ่านไฟล์ไม่สำเร็จ"));
    reader.readAsDataURL(file);
  });

export const api = {
  user: {
    me: () => dual("user.me", {}, async () => adaptUserMe(await gs("me", {}))),
    update: (data) =>
      dual("user.update", { data }, async () =>
        gs("meUpdate", { ...(data.displayName ? { displayName: data.displayName } : {}), ...(data.username ? { username: data.username } : {}), ...(data.description !== undefined ? { bio: data.description } : {}) })
      ),
    /** GS ไม่มี action เฉพาะ — เก็บใน preferences.termsAcceptedAt (me.update) */
    acceptTerms: (version) => dual("user.acceptTerms", { version }, () => mergePreferences({ termsAcceptedAt: new Date().toISOString(), termsVersion: version })),
    savePreferences: (data) => dual("user.preferences", { data }, () => mergePreferences(data)),
  },
  /** อัปโหลดรูป (≤5MB) คืน { url } — mock ไม่เก็บไฟล์ */
  upload: async (file, type = "character") => {
    if (file.size > 5 * 1024 * 1024) throw new Error("ไฟล์ใหญ่เกิน 5MB");
    const mime = (file.type || "").replace("image/", "");
    if (!["jpeg", "png", "gif", "webp"].includes(mime)) throw new Error("รองรับเฉพาะ JPG / PNG / GIF / WEBP");
    if (IS_MOCK) return { url: "" };
    return gs("upload", { type, mime, name: file.name, data: await fileToBase64(file) });
  },

  characters: {
    list: (p = {}) =>
      dual("chars.list", p, async () => {
        const tags = await loadTags();
        const tagsCsv = String(p.tags || "").split(",").filter(Boolean);
        const r = await gs("charList", { ...p, tags: tagIdsOf(tagsCsv, tags).join(",") });
        return { ...r, items: (r.items || []).map((c) => adaptCard(c, tags)) };
      }),
    get: (id) => dual("chars.get", { id }, async () => adaptCard(await gs("charGet", { id }), await loadTags())),
    toggleLike: (id) => dual("chars.like", { id }, () => gs("like", { targetType: "character", targetId: id })),
    create: (data) =>
      dual("chars.create", { data }, async () => {
        const tags = await loadTags();
        const { tags: names, ...rest } = data;
        const r = await gs("charSave", { ...rest, tagIds: tagIdsOf(names, tags), contentRating: "PG", flag: rest.flag || "green" });
        return r.character || r;
      }),
    mine: () => dual("chars.myList", {}, async () => (await gs("charMine", {})).items || []),
  },

  universes: {
    list: (p = {}) =>
      dual("univ.list", p, async () => {
        const r = await gs("universeList", p);
        return { ...r, items: (r.items || []).map((u) => ({ ...u, creator: u.createdBy ? { id: u.createdBy, username: u.creatorName, displayName: u.creatorName } : undefined })) };
      }),
    get: (id) =>
      dual("univ.get", { id }, async () => {
        const r = await gs("universeGet", { id });
        const tags = await loadTags();
        return { ...r.universe, creator: { id: r.universe.createdBy, username: r.universe.creatorName, displayName: r.universe.creatorName }, characters: (r.characters || []).map((c) => adaptCard(c, tags)) };
      }),
  },

  creators: {
    /** GS ไม่มี creators.list — สรุปจาก characters.list ที่ยอดนิยม (ประมาณการ) */
    list: ({ q } = {}) =>
      dual("creators.list", { q }, async () => {
        const r = await gs("charList", { sort: "popular", limit: 50 });
        const map = new Map();
        (r.items || []).forEach((c) => {
          if (!c.createdBy) return;
          const cur = map.get(c.createdBy) || { id: c.createdBy, username: c.creatorName, displayName: c.creatorName || "ครีเอเตอร์", characterCount: 0, messageCount: 0 };
          cur.characterCount += 1;
          cur.messageCount += Number(c.messageCount) || 0;
          map.set(c.createdBy, cur);
        });
        const items = [...map.values()].filter((c) => !q || (c.displayName || "").toLowerCase().includes(String(q).toLowerCase()));
        return { items: items.sort((a, b) => b.messageCount - a.messageCount) };
      }),
    get: (id) =>
      dual("creators.get", { id }, async () => {
        const r = await gs("profile", { userId: id });
        const tags = await loadTags();
        return { ...r.user, id: r.user.id || id, isFollowing: r.isFollowing, characters: (r.characters || []).map((c) => adaptCard(c, tags)), characterCount: r.user.characterCount ?? (r.characters || []).length };
      }),
    toggleFollow: (userId) => dual("follow.toggle", { userId }, () => gs("follow", { userId })),
  },

  /** GS ไม่มี rankings — สรุปจาก list ยอดนิยม (ประมาณการ) */
  ranking: {
    overview: () =>
      dual("ranking.overview", {}, async () => {
        const [cr, ur] = await Promise.all([gs("charList", { sort: "popular", limit: 50 }), gs("universeList", { sort: "popular", limit: 10 })]);
        const chars = (cr.items || []).map((c, i) => ({ rank: i + 1, id: c.id, name: c.name, image: c.image, value: c.messageCount || 0 }));
        const creators = new Map();
        (cr.items || []).forEach((c) => {
          if (c.createdBy) creators.set(c.createdBy, { id: c.createdBy, name: c.creatorName || "ครีเอเตอร์", value: (creators.get(c.createdBy)?.value || 0) + (Number(c.messageCount) || 0) });
        });
        return {
          period: "week",
          characters: chars.slice(0, 10),
          universes: (ur.items || []).map((u, i) => ({ rank: i + 1, id: u.id, name: u.name, image: u.image, value: u.messageCount || 0 })),
          creators: [...creators.values()].sort((a, b) => b.value - a.value).slice(0, 10).map((c, i) => ({ ...c, rank: i + 1 })),
        };
      }),
  },

  chats: {
    /** รายการแบบง่าย (sidebar) */
    list: (p = {}) => bothRead("chatList", { limit: 50, ...p }).then((r) => (r.items || []).map(adaptChat)),
    /** รายการแบบแบ่งหน้า: { items, nextCursor } — archived / q / cursor */
    listPage: (p = {}) => bothRead("chatList", { limit: 50, ...p }).then((r) => ({ items: (r.items || []).map(adaptChat), nextCursor: r.nextCursor || null })),
    get: (chatId) => bothRead("chatGet", { id: chatId }).then((r) => adaptChat({ ...r.chat, character: r.character })),
    /** ข้อมูลเต็มของห้อง (ตัวละคร/บทบาท/สถานการณ์/โน้ต/สถิติ/ความสัมพันธ์) */
    detail: (chatId) => bothRead("chatGet", { id: chatId }).then(adaptChatDetail),
    create: (characterId, opts = {}) => both("chatCreate", { characterId, ...opts }).then((r) => adaptChat(r.chat)),
    update: (chatId, data) => both("chatUpdate", { id: chatId, ...data }).then((r) => r.chat),
    remove: (chatId) => both("chatDelete", { id: chatId }),
    /** หน้าแรกของข้อความ (เก่า->ใหม่) */
    messages: (chatId) => both("messages", { chatId, limit: 30 }).then((r) => r.items || []),
    /** แบ่งหน้าข้อความ: ส่ง cursor (= nextCursor ที่ได้รับ) เพื่อโหลดข้อความที่เก่ากว่า */
    messagesPage: (chatId, cursor) => both("messages", { chatId, limit: 30, ...(cursor ? { cursor } : {}) }).then((r) => ({ items: r.items || [], nextCursor: r.nextCursor || null })),
    send: (chatId, content, modelId, replyToId, onToken) =>
      both("send", { chatId, content, modelId, replyToId, ...(onToken ? { onToken } : {}) }).then((r) => ({
        messages: [r.userMessage, { ...r.aiMessage, romanticDelta: r.aiMessage?.romanticDelta ?? r.delta?.romantic, intimacyDelta: r.aiMessage?.intimacyDelta ?? r.delta?.intimacy }],
        userMessage: r.userMessage,
        aiMessage: { ...r.aiMessage, romanticDelta: r.aiMessage?.romanticDelta ?? r.delta?.romantic, intimacyDelta: r.aiMessage?.intimacyDelta ?? r.delta?.intimacy },
        coinCost: Number(r.coinCost) || 0,
        delta: r.delta || {},
        chat: r.chat,
        tokensLeft: r.coins,
        mock: r.mock,
      })),
    /** เจนข้อความ AI ล่าสุดใหม่ (หักคอยน์ตามโมเดล) */
    regenerate: (chatId, modelId, onToken) => both("regenerate", { chatId, modelId, ...(onToken ? { onToken } : {}) }).then((r) => ({ aiMessage: r.aiMessage, coinCost: Number(r.coinCost) || 0, tokensLeft: r.coins })),
    react: (chatId, id, reaction) => both("messageReact", { chatId, id, reaction }).then((r) => r.message),
    memory: {
      get: (chatId) => both("memoryGet", { chatId }),
      save: (chatId, text, isActive) => both("memorySave", { chatId, text, ...(isActive === undefined ? {} : { isActive }) }),
    },
  },

  /** สิทธิ์สร้างตัวละคร : ซื้อด้วย QR แบบเดียวกับเติมคอยน์ ; เผยแพร่ตัวละครครั้งแรกหัก 1 สิทธิ์ */
  credits: {
    init: (transactionId, qty) => both("creditInit", { transactionId, qty }),
    confirm: (transactionId) => both("creditConfirm", { transactionId }),
    history: () => both("creditHistory", { limit: 20 }).then((r) => r.items || []),
  },

  personas: {
    list: (scope = "mine") => both("personaList", { scope }).then((r) => r.items || []),
    save: (data) => both("personaSave", data).then((r) => r.persona),
  },
  scenarios: {
    list: (scope = "mine", characterId) => both("scenarioList", { scope, ...(characterId ? { characterId } : {}) }).then((r) => r.items || []),
    save: (data) => both("scenarioSave", data).then((r) => r.scenario),
  },

  coins: {
    packages: () => fromBoot("packages", async () => (await gs("packages", {})).items || []).then((rows) => rows.map(adaptPackage)),
    /** เรียกทันทีหลังสร้าง QR สำเร็จ (ผูก transaction กับผู้ใช้+แพ็ก) */
    topupInit: (transactionId, packageId, amount) => both("topupInit", { transactionId, packageId, ...(amount ? { amount } : {}) }),
    /** ตั้งค่าราคา (ช่วงกำหนดเอง/ขั้นโบนัส/ขั้นส่วนลดสิทธิ์สร้างตัวละคร) + สิทธิ์สร้างตัวละครคงเหลือ — ค่ามาจาก settings ใน DB */
    pricing: () => both("pricing", {}),
    /** เรียกหลัง PaymentBroker แจ้งจ่ายสำเร็จ — ยังไม่เจอการจ่าย GS ตอบ code=payment_pending (ลองซ้ำได้ปลอดภัย) */
    confirm: (packageId, transactionId) => both("topupConfirm", { transactionId, packageId }).then((r) => ({ ...r, tokensLeft: r.balance })),
    /** รายการเติมเงินของฉัน (ใช้หารายการค้าง pending) */
    topups: () => both("topupsMine", { limit: 10 }).then((r) => r.items || []),
    history: (type) =>
      dual("coins.historyList", { type }, async () => {
        const items = ((await gs("coinHistory", { limit: 50 })).items || []).map((t) => ({ ...t, coinUsed: Number(t.amount) || 0 }));
        return items.filter((t) => !type || type === "all" || (type === "in" ? t.coinUsed > 0 : t.coinUsed < 0));
      }),
    /** ประวัติแบบแบ่งหน้า { items:[{id,usageType,coinUsed,balanceAfter,note,createdAt}], nextCursor } — usageType กรองที่ server */
    historyPage: ({ cursor, usageType, limit = 20 } = {}) =>
      bothRead("coinHistory", { limit, ...(cursor ? { cursor } : {}), ...(usageType ? { usageType } : {}) }).then((r) => ({
        items: (r.items || []).map((t) => ({ ...t, coinUsed: Number(t.amount ?? t.coinUsed) || 0 })),
        nextCursor: r.nextCursor || null,
      })),
    club: () => bothRead("vip", {}).then(adaptVip),
    claimMonthly: () => both("vipClaim", {}).then((r) => ({ ...r, tokensLeft: r.balance })),
  },
  models: { list: () => fromBoot("models", async () => (await gs("models", {})).items || []) },
  gifts: {
    catalog: () => fromBoot("gifts", async () => (await gs("gifts", {})).items || []).then((rows) => rows.map(adaptGift)),
    send: (chatId, giftId) =>
      both("giftSend", { chatId, giftId }).then((r) => ({ ...r, messages: [r.message, r.aiMessage].filter(Boolean), tokensLeft: r.coins })),
  },

  rewards: {
    checkinStatus: () => bothRead("checkinStatus", {}).then(adaptCheckin),
    checkin: () => both("checkinClaim", {}).then((r) => ({ ...r, coins: (r.coins || 0) + (r.bonus || 0), tokensLeft: r.balance })),
    redeem: (code) => both("promo", { code }).then((r) => ({ ...r, tokensLeft: r.balance })),
    invite: () =>
      Promise.all([bothRead("referralInfo", {}), loadBoot().catch(() => null)]).then(([r, b]) => {
        const st = b?.settings || {}; // ค่าสาธารณะจาก bootstrap (referral_*)
        return {
          code: r.code, invitedCount: r.invited || 0, coinsEarned: r.earned || 0, maxFriends: r.maxInvites || Number(st.referral_max_invites) || 5,
          perFriend: Number(st.referral_inviter_coins) || 50, friendGets: Number(st.referral_invitee_coins) || 50, referredBy: r.referredBy, history: r.history || [],
        };
      }),
    applyReferral: (code) => both("referralApply", { code }).then((r) => ({ ...r, tokensLeft: r.balance })),
  },

  notifications: {
    list: () => dual("notif.list", {}, async () => (await gs("notifList", { limit: 30 })).items || []),
    markAll: () => dual("notif.read", { all: true }, () => gs("notifRead", { all: true })),
  },
  feed: {
    list: () =>
      dual("feed.list", {}, async () =>
        ((await gs("postList", { type: "post", limit: 20 })).items || []).map((p) => ({ ...p, creator: { id: p.userId, displayName: p.userName, image: p.userPhoto } }))
      ),
    shorts: () =>
      dual("shorts.list", {}, async () =>
        ((await gs("postList", { type: "short", limit: 20 })).items || []).map((p) => ({ ...p, characterId: (p.characterIds || [])[0], name: p.userName, caption: p.text }))
      ),
  },
  admin: {
    stats: () => dual("admin.stats", {}, () => gs("adminStats", { days: 14 })),
    users: () =>
      dual("admin.users", {}, async () => ((await gs("adminUsers", { limit: 50 })).items || []).map((u) => ({ ...u, user_type: u.userType, tokensLeft: u.coins }))),
  },
};

export default api;
